import { apiClient } from '../apiClient';

// ─── Domain types ─────────────────────────────────────────────────────────────
// Field names mirror the JSON tags in
// services/api/internal/hackathons/model.go — keep in sync.

export type HackathonStatus = 'draft' | 'scheduled' | 'live' | 'completed';
export type HackathonComponentType = 'problem' | 'project';

export interface Hackathon {
  id: string;
  batch_id: string;
  org_id: string;
  title: string;
  description?: string;
  team_size: number;
  start_at: string;
  end_at: string;
  status: HackathonStatus;
  created_by: string;
  created_at: string;
  module_tags?: string[];
}

export interface HackathonComponent {
  id: string;
  hackathon_id: string;
  type: HackathonComponentType;
  max_points: number;
  problem_id?: string;
  problem_title?: string;
  project_title?: string;
  project_brief?: string;
  project_format?: string;
  problem_slug?: string;
  display_order: number;
}

export interface HackathonTeam {
  id: string;
  hackathon_id: string;
  name: string;
  member_ids: string[];
}

export interface HackathonSubmission {
  id: string;
  component_id: string;
  team_id: string;
  submission_id?: string | null;
  project_url?: string | null;
  score?: number | null;
  feedback?: string | null;
  graded_by?: string | null;
  graded_at?: string | null;
  submitted_at: string;
}

export interface RankEntry {
  rank: number;
  team_id: string;
  team_name: string;
  score: number;
}

export interface HackathonProjectSubmission {
  id: string;
  component_id: string;
  team_id: string;
  submitted_by: string;
  repo_url?: string;
  demo_url?: string;
  video_url?: string;
  file_key?: string;
  description?: string;
  submitted_at: string;
}

export interface MyHackathonTeam {
  team_id: string;
  name: string;
  usernames: string[];
}

export interface SubmitProjectFields {
  repo_url?: string;
  demo_url?: string;
  video_url?: string;
  description?: string;
  file?: File;
}

// ─── Request payloads ──────────────────────────────────────────────────────────

export interface CreateHackathonRequest {
  title: string;
  description?: string;
  team_size: number;
  start_at: string; // RFC3339
  end_at: string; // RFC3339
  module_ids?: string[];
}

export interface AddComponentRequest {
  type: HackathonComponentType;
  max_points: number;
  problem_id?: string;
  project_title?: string;
  project_brief?: string;
  project_format?: string;
}

export interface AssignTeamsRequest {
  teams: { name: string; member_ids: string[] }[];
}

export interface GradeSubmissionRequest {
  score: number;
  feedback: string;
}

export interface StatusResponse {
  status: string;
}

export interface UpdateStatusRequest {
  status: HackathonStatus;
}

// ─── Trainer API (full CRUD + grading) ──────────────────────────────────────────

export const hackathonsApi = {
  listByBatch: (batchId: string) =>
    apiClient.get<Hackathon[]>(`/api/v1/trainer/batches/${batchId}/hackathons`),

  create: (batchId: string, req: CreateHackathonRequest) =>
    apiClient.post<Hackathon>(`/api/v1/trainer/batches/${batchId}/hackathons`, req),

  get: (batchId: string, hackathonId: string) =>
    apiClient.get<Hackathon>(`/api/v1/trainer/batches/${batchId}/hackathons/${hackathonId}`),

  addComponent: (batchId: string, hackathonId: string, req: AddComponentRequest) =>
    apiClient.post<HackathonComponent>(
      `/api/v1/trainer/batches/${batchId}/hackathons/${hackathonId}/components`,
      req
    ),

  listComponents: (batchId: string, hackathonId: string) =>
    apiClient.get<HackathonComponent[]>(
      `/api/v1/trainer/batches/${batchId}/hackathons/${hackathonId}/components`
    ),

  assignTeams: (batchId: string, hackathonId: string, req: AssignTeamsRequest) =>
    apiClient.post<StatusResponse>(
      `/api/v1/trainer/batches/${batchId}/hackathons/${hackathonId}/teams`,
      req
    ),

  listTeams: (batchId: string, hackathonId: string) =>
    apiClient.get<HackathonTeam[]>(
      `/api/v1/trainer/batches/${batchId}/hackathons/${hackathonId}/teams`
    ),

  updateStatus: (batchId: string, hackathonId: string, req: UpdateStatusRequest) =>
    apiClient.patch<StatusResponse>(
      `/api/v1/trainer/batches/${batchId}/hackathons/${hackathonId}/status`,
      req
    ),

  gradeSubmission: (
    batchId: string,
    hackathonId: string,
    submissionId: string,
    req: GradeSubmissionRequest
  ) =>
    apiClient.post<StatusResponse>(
      `/api/v1/trainer/batches/${batchId}/hackathons/${hackathonId}/submissions/${submissionId}/grade`,
      req
    ),

  getLeaderboard: (batchId: string, hackathonId: string) =>
    apiClient.get<RankEntry[]>(
      `/api/v1/trainer/batches/${batchId}/hackathons/${hackathonId}/leaderboard`
    ),
};

// ─── Org-admin API (read-only) ───────────────────────────────────────────────────

export const orgHackathonsApi = {
  listByBatch: (batchId: string) =>
    apiClient.get<Hackathon[]>(`/api/v1/org/batches/${batchId}/hackathons`),

  getLeaderboard: (batchId: string, hackathonId: string) =>
    apiClient.get<RankEntry[]>(
      `/api/v1/org/batches/${batchId}/hackathons/${hackathonId}/leaderboard`
    ),
};

// ─── Student / LMS API (read-only) ───────────────────────────────────────────────

export const studentHackathonsApi = {
  listByBatch: (batchId: string) =>
    apiClient.get<Hackathon[]>(`/api/v1/lms/batches/${batchId}/hackathons`),

  get: (batchId: string, hackathonId: string) =>
    apiClient.get<Hackathon>(`/api/v1/lms/batches/${batchId}/hackathons/${hackathonId}`),

  listComponents: (batchId: string, hackathonId: string) =>
    apiClient.get<HackathonComponent[]>(
      `/api/v1/lms/batches/${batchId}/hackathons/${hackathonId}/components`
    ),

  getLeaderboard: (batchId: string, hackathonId: string) =>
    apiClient.get<RankEntry[]>(
      `/api/v1/lms/batches/${batchId}/hackathons/${hackathonId}/leaderboard`
    ),

  submitProject: (
    batchId: string,
    hackathonId: string,
    componentId: string,
    fields: SubmitProjectFields
  ) => {
    const form = new FormData();
    if (fields.repo_url) form.append('repo_url', fields.repo_url);
    if (fields.demo_url) form.append('demo_url', fields.demo_url);
    if (fields.video_url) form.append('video_url', fields.video_url);
    if (fields.description) form.append('description', fields.description);
    if (fields.file) form.append('file', fields.file);
    return apiClient.post<HackathonProjectSubmission>(
      `/api/v1/lms/batches/${batchId}/hackathons/${hackathonId}/components/${componentId}/submit`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },

  listProjectSubmissions: (batchId: string, hackathonId: string, componentId: string) =>
    apiClient.get<HackathonProjectSubmission[]>(
      `/api/v1/lms/batches/${batchId}/hackathons/${hackathonId}/components/${componentId}/submissions`
    ),

  getMyTeam: (batchId: string, hackathonId: string) =>
    apiClient.get<MyHackathonTeam>(
      `/api/v1/lms/batches/${batchId}/hackathons/${hackathonId}/my-team`
    ),
};
